import { ApiFormattedResponse } from "@models/api_formatted_response"
import { GraphQLBody } from "@models/graphql_body"
import { mutate, query } from "@utils/graphql"
import {
  CountryResponse,
  Customer,
  CustomerBillingAddresses,
  CustomerBillingAddressesInput,
  CustomerBillingAddressesOutput,
  CustomerInvoice,
  CustomerInvoicePaginatedResponse,
  CustomerInvoicesPaginatedData,
  CustomerOnboardingInfoOutput,
  CustomerPaymentCard,
  CustomerPaymentCardOutput,
  CustomerPerson,
  CustomerPersonResponse,
  CustomerPersonTokenForAdmin,
  CustomerPersonsListResponse,
  CustomerPersonsPaginate,
  CustomerProduct,
  CustomerProductResponse,
  CustomerResponse,
  CustomerType,
  DeleteCustomerPersonInput,
  EditCPersonData,
  EditCPersonResponse,
  GetCountriesInput,
  GetCustomerInvoicesInput,
  GetCustomerSessionResponse,
  GetStatesProvincesCitiesInput,
  MutationUpdateCustomerPersonArgs,
  PaginatedCountry,
  PaginatedStateProvinceCity,
  Product,
  ProductResponse,
  QueryCustomerPersonsArgs,
  QueryGetLocationCountriesArgs,
  QueryGetLocationStatesProvincesCitiesArgs,
  RetryPaymentInput,
  AddCouponCodeToCustomerInput,
  RemoveCouponCodeFromCustomerInput,
  SpecialOfferPackageInput,
  StatesProvincesCitiesResponse,
  UpdateCustomerInput,
  UpdateCustomerInvoiceInput,
  UpdateCustomerInvoiceResponse,
  UpdateCustomerPersonInput,
  UpdateCustomerProductInput,
  UpdateCustomerProductPlanInput
} from "__generated__/graphql"
import cancelSpecialOfferPackage from "__generated__/queries/mutations/cancelSpecialOfferPackage"
import changeAccountStatus from "__generated__/queries/mutations/changeAccountStatus"
import createCustomerBillingAddresses from "__generated__/queries/mutations/createCustomerBillingAddresses"
import deleteCustomer from "__generated__/queries/mutations/deleteCustomer"
import deleteCustomerPerson from "__generated__/queries/mutations/deleteCustomerPerson"
import getCustomerSessionByAdmin from "__generated__/queries/mutations/getCustomerSessionByAdmin"
import retryPayment from "__generated__/queries/mutations/retryPayment"
import addCouponCodeToCustomer from "__generated__/queries/mutations/addCouponCodeToCustomer"
import removeCouponCodeFromCustomer from "__generated__/queries/mutations/removeCouponCodeFromCustomer"
import sendSpecialOfferPackage from "__generated__/queries/mutations/sendSpecialOfferPackage"
import updateCustomer from "__generated__/queries/mutations/updateCustomer"
import updateCustomerInvoice from "__generated__/queries/mutations/updateCustomerInvoice"
import updateCustomerOnboardingInfo from "__generated__/queries/mutations/updateCustomerOnboardingInfo"
import updateCustomerPerson from "__generated__/queries/mutations/updateCustomerPerson"
import updateCustomerProduct from "__generated__/queries/mutations/updateCustomerProduct"
import updateCustomerProductPlan from "__generated__/queries/mutations/updateCustomerProductPlan"
import updateSpecialOfferPackage from "__generated__/queries/mutations/updateSpecialOfferPackage"
import verifyEmail from "__generated__/queries/mutations/verifyEmail"
import customerPersons from "__generated__/queries/queries/customerPersons"
import getCustomerById from "__generated__/queries/queries/getCustomerById"
import getCustomerBillingAddresses from "__generated__/queries/queries/getCustomerBillingAddresses"
import getCustomerInvoices from "__generated__/queries/queries/getCustomerInvoices"
import getCustomerOnboardingInfo from "__generated__/queries/queries/getCustomerOnboardingInfo"
import getCustomerPaymentCard from "__generated__/queries/queries/getCustomerPaymentCard"
import getLocationCountries from "__generated__/queries/queries/getLocationCountries"
import getLocationStatesProvincesCities from "__generated__/queries/queries/getLocationStatesProvincesCities"

export type UpdateCOnboardingInfoInput = {
  customer: string
  companyIndustry?: string
  companyName?: string
  companySize?: string
  companyWebsite?: string
  favouriteProducts?: string
  howBuiltWebsite?: string
  howYouHearAboutUs?: string
  ownerRole?: string
}
export type CustomerIdData = {
  customerId: string
}
export type PaginationData = {
  pageSize: number
  page: number
  search?: string
}
export type UpdateUserData = {
  _id: string
  customerId: string
  email: string
  role: CustomerType
}
export type DeleteUserData = {
  _id: string
  customerId: string
}
export type VerifyUserEmail = {
  _id: string
  customerId: string
  isVerified: boolean
}

export type AdminUpdateCustomerProductInput = UpdateCustomerProductInput & {
  directAccountBalance?: number
}

export async function getCustomersOnboarding(
  customerId: string
): Promise<ApiFormattedResponse<CustomerOnboardingInfoOutput>> {
  const response = await query<{
    getCustomerOnboardingInfo?: CustomerOnboardingInfoOutput
  }>({
    query: getCustomerOnboardingInfo,
    variables: {
      customerId: customerId
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: { data: response?.data?.getCustomerOnboardingInfo?.data }
  }
}

export async function getCustomerByIdQuery(
  customerId: string
): Promise<ApiFormattedResponse<Customer>> {
  const response = await query<{
    getCustomerById?: CustomerResponse
  }>({
    query: getCustomerById,
    variables: {
      customerId
    },
    fetchPolicy: "network-only"
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response?.data?.getCustomerById?.data as Customer
  }
}

export async function goToCustomerSessionByAdmin(data: {
  customerId: string
}): Promise<ApiFormattedResponse<GetCustomerSessionResponse>> {
  const response = await query<{
    getCustomerSessionByAdmin?: GetCustomerSessionResponse
  }>({
    query: getCustomerSessionByAdmin,
    variables: { data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: {
      data: response?.data?.getCustomerSessionByAdmin
        ?.data as CustomerPersonTokenForAdmin
    }
  }
}

export async function updateCustomersOnboarding(
  data: UpdateCOnboardingInfoInput
): Promise<ApiFormattedResponse<CustomerOnboardingInfoOutput>> {
  const response = await query<{
    updateCustomerOnboardingInfo?: CustomerOnboardingInfoOutput
  }>({
    query: updateCustomerOnboardingInfo,
    variables: { data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: { data: response?.data?.updateCustomerOnboardingInfo?.data }
  }
}

export async function accountGetCustomerBillingAddresses(
  data: CustomerIdData
): Promise<ApiFormattedResponse<CustomerBillingAddresses>> {
  const response = await query<
    {
      getCustomerBillingAddresses?: CustomerBillingAddressesOutput
    },
    CustomerIdData
  >({
    query: getCustomerBillingAddresses,
    variables: {
      customerId: data.customerId
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  //todo: take a look at the backend
  return {
    isSuccess: true,
    data: response?.data?.getCustomerBillingAddresses
      ?.data as CustomerBillingAddresses
  }
}

export async function accountGetCustomerPaymentCard(
  data: CustomerIdData
): Promise<ApiFormattedResponse<CustomerPaymentCard>> {
  const response = await query<
    {
      getCustomerPaymentCard?: CustomerPaymentCardOutput
    },
    CustomerIdData
  >({
    query: getCustomerPaymentCard,
    variables: {
      customerId: data.customerId
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.getCustomerPaymentCard?.data as CustomerPaymentCard
  }
}

export async function getCustomerInvoicesQuery(
  input: GetCustomerInvoicesInput
): Promise<ApiFormattedResponse<CustomerInvoicesPaginatedData>> {
  const response = await query<{
    getCustomerInvoices: CustomerInvoicePaginatedResponse
  }>({
    query: getCustomerInvoices,
    variables: { input }
  })
  return {
    ...response,
    data: response.isSuccess
      ? response.data?.getCustomerInvoices?.data
      : undefined
  }
}
export async function customersGetUsers(
  data: CustomerIdData & PaginationData
): Promise<ApiFormattedResponse<CustomerPersonsPaginate>> {
  const response = await query<
    {
      customerPersons?: CustomerPersonsListResponse
    },
    QueryCustomerPersonsArgs
  >({
    query: customerPersons,
    variables: {
      customerId: data.customerId,
      pageSize: data.pageSize,
      page: data.page,
      search: data.search
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response.data?.customerPersons?.data
  }
}

export async function customersUpdateUser(
  data: UpdateUserData
): Promise<ApiFormattedResponse<UpdateCustomerPersonInput>> {
  const response = await mutate<
    {
      updateCustomerPerson?: MutationUpdateCustomerPersonArgs
    },
    GraphQLBody<UpdateCustomerPersonInput>
  >({
    mutation: updateCustomerPerson,
    variables: {
      data: {
        _id: data._id,
        customerId: data.customerId,
        type: data.role,
        email: data.email
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.updateCustomerPerson?.data
  }
}

export async function customersVerifyUser(
  data: VerifyUserEmail
): Promise<ApiFormattedResponse<UpdateCustomerPersonInput>> {
  const response = await mutate<
    {
      updateCustomerPerson?: MutationUpdateCustomerPersonArgs
    },
    GraphQLBody<UpdateCustomerPersonInput>
  >({
    mutation: updateCustomerPerson,
    variables: {
      data: {
        _id: data._id,
        customerId: data.customerId,
        isEmailVerified: data.isVerified
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.updateCustomerPerson?.data
  }
}
export async function customersDeleteUser(
  data: DeleteUserData
): Promise<ApiFormattedResponse<CustomerPerson>> {
  const response = await mutate<
    {
      deleteCustomerPerson?: CustomerPersonResponse
    },
    GraphQLBody<DeleteCustomerPersonInput>
  >({
    mutation: deleteCustomerPerson,
    variables: {
      input: {
        _id: data._id,
        customerId: data.customerId
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.deleteCustomerPerson?.data
  }
}
export async function verifyUserEmail(data: {
  code: string
  firstName: string
  lastName: string
  password: string
}): Promise<ApiFormattedResponse<EditCPersonData>> {
  const response = await mutate<{
    verifyEmail?: EditCPersonResponse
  }>({
    mutation: verifyEmail,
    variables: { data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.verifyEmail?.data
  }
}

export async function updateCustomerProducts(
  data: AdminUpdateCustomerProductInput
): Promise<ApiFormattedResponse<CustomerProduct>> {
  const response = await mutate<{
    updateCustomerProduct?: CustomerProductResponse
  }>({
    mutation: updateCustomerProduct,
    variables: { data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.updateCustomerProduct?.data
  }
}

export async function updateExtendReducePlan(
  data: UpdateCustomerProductPlanInput
): Promise<ApiFormattedResponse<Customer>> {
  const response = await mutate<{
    updateCustomerProductPlan?: CustomerResponse
  }>({
    mutation: updateCustomerProductPlan,
    variables: { data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.updateCustomerProductPlan?.data
  }
}

// `operatorCredits` is missing from the generated input until codegen runs against the new api-v2.
export type SpecialOfferInput = SpecialOfferPackageInput & {
  operatorCredits?: number | null
}

export async function specialOfferPackage(
  data: SpecialOfferInput
): Promise<ApiFormattedResponse<Product>> {
  const response = await mutate<{
    sendSpecialOfferPackage?: ProductResponse
  }>({
    mutation: sendSpecialOfferPackage,
    variables: { data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response.data?.sendSpecialOfferPackage?.data
  }
}

export async function updateMutationSpecialOffer(
  data: SpecialOfferInput
): Promise<ApiFormattedResponse<Product>> {
  const response = await mutate<{
    updateSpecialOfferPackage?: ProductResponse
  }>({
    mutation: updateSpecialOfferPackage,
    variables: { data }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.updateSpecialOfferPackage?.data
  }
}
export async function updateCustomerStatus(data: {
  customerId: string
  deactivate: boolean
}): Promise<ApiFormattedResponse<Customer>> {
  const response = await mutate<{
    changeAccountStatus?: CustomerResponse
  }>({
    mutation: changeAccountStatus,
    variables: {
      data: {
        customerId: data.customerId,
        deactivate: data.deactivate
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.changeAccountStatus?.data
  }
}

export async function deleteSpecialOffer(data: {
  customerId: string
}): Promise<ApiFormattedResponse<Product>> {
  const response = await mutate<{
    cancelSpecialOfferPackage?: ProductResponse
  }>({
    mutation: cancelSpecialOfferPackage,
    variables: { customerId: data.customerId }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.cancelSpecialOfferPackage?.data
  }
}

export async function updateCustomerMutation(
  data: UpdateCustomerInput
): Promise<ApiFormattedResponse<Customer>> {
  const response = await mutate<
    {
      updateCustomer?: CustomerResponse
    },
    GraphQLBody<UpdateCustomerInput>
  >({
    mutation: updateCustomer,
    variables: {
      data
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.updateCustomer?.data
  }
}

export async function deleteCustomerMutation(data: {
  customerId: string
}): Promise<ApiFormattedResponse<Customer>> {
  const response = await mutate<{
    deleteCustomer?: CustomerResponse
  }>({
    mutation: deleteCustomer,
    variables: { data: { customerId: data.customerId } }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.deleteCustomer?.data
  }
}

export async function accountCreateCustomerBillingAddresses(
  data: CustomerBillingAddressesInput
): Promise<ApiFormattedResponse<CustomerBillingAddresses>> {
  const response = await mutate<
    {
      createCustomerBillingAddresses?: CustomerBillingAddressesOutput
    },
    GraphQLBody<CustomerBillingAddressesInput>
  >({
    mutation: createCustomerBillingAddresses,
    variables: {
      data: data
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  //todo: take a look at the backend
  return {
    isSuccess: true,
    data: response?.data?.createCustomerBillingAddresses
      ?.data as CustomerBillingAddresses
  }
}

export async function getCountries(
  data: GetCountriesInput
): Promise<ApiFormattedResponse<PaginatedCountry>> {
  const response = await query<
    {
      getLocationCountries?: CountryResponse
    },
    QueryGetLocationCountriesArgs
  >({
    query: getLocationCountries,
    variables: {
      params: {
        pageNumber: data.pageNumber,
        pageSize: data.pageSize,
        search: data.search
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  //todo: take a look at the backend
  return {
    isSuccess: true,
    data: response?.data?.getLocationCountries?.data as PaginatedCountry
  }
}

export async function getLocations(
  data: GetStatesProvincesCitiesInput
): Promise<ApiFormattedResponse<PaginatedStateProvinceCity>> {
  const response = await query<
    {
      getLocationStatesProvincesCities?: StatesProvincesCitiesResponse
    },
    QueryGetLocationStatesProvincesCitiesArgs
  >({
    query: getLocationStatesProvincesCities,
    variables: {
      params: {
        pageNumber: data.pageNumber,
        pageSize: data.pageSize,
        search: data.search,
        countryId: data.countryId,
        stateProvinceId: data.stateProvinceId,
        type: data.type
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.getLocationStatesProvincesCities?.data
  }
}

export async function updateCustomerInvoiceMutation(
  data: UpdateCustomerInvoiceInput
): Promise<ApiFormattedResponse<CustomerInvoice>> {
  const response = await mutate<{
    updateCustomerInvoice?: UpdateCustomerInvoiceResponse
  }>({
    mutation: updateCustomerInvoice,
    variables: {
      data
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.updateCustomerInvoice?.data as CustomerInvoice
  }
}

export async function retryPaymentMutation(
  data: RetryPaymentInput
): Promise<ApiFormattedResponse<CustomerProduct>> {
  const response = await mutate<
    {
      retryPayment?: CustomerProductResponse
    },
    GraphQLBody<RetryPaymentInput>
  >({
    mutation: retryPayment,
    variables: {
      input: data
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response?.data?.retryPayment?.data
  }
}

export async function addCouponCodeToCustomerMutation(
  data: AddCouponCodeToCustomerInput
): Promise<ApiFormattedResponse<CustomerProduct>> {
  const response = await mutate<
    {
      addCouponCodeToCustomer?: CustomerProductResponse
    },
    GraphQLBody<AddCouponCodeToCustomerInput>
  >({
    mutation: addCouponCodeToCustomer,
    variables: { data }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response?.data?.addCouponCodeToCustomer?.data
  }
}

export async function removeCouponCodeFromCustomerMutation(
  data: RemoveCouponCodeFromCustomerInput
): Promise<ApiFormattedResponse<CustomerProduct>> {
  const response = await mutate<
    {
      removeCouponCodeFromCustomer?: CustomerProductResponse
    },
    GraphQLBody<RemoveCouponCodeFromCustomerInput>
  >({
    mutation: removeCouponCodeFromCustomer,
    variables: { data }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response?.data?.removeCouponCodeFromCustomer?.data
  }
}
